export interface BreadcrumbItem {
  title: string;
  path?: string;
}

// Utility functions
function pathSegments(location: string): string[] {
  const path = location.split('?')[0];
  return path.split('/').filter(Boolean);
}

function titleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function formatTitle(title: string): string {
  const words = title.split('-').filter((w) => w.toLowerCase() !== 'all');
  return words
    .map((word) => {
      if (
        word.toLowerCase() === 'and' ||
        (/^[A-Z]{2,3}$/.test(word) && word.length <= 3)
      ) {
        return word;
      }
      return titleCase(word);
    })
    .join(' ');
}

function pluckSearchTerm(location: string): string | null {
  const path = location.split('?')[0];
  const match = path.match(/\/.*?\/search\/(.*)/);
  if (match) {
    return formatTitle(decodeURIComponent(match[1]));
  }
  return null;
}

function getQueryParam(location: string, param: string): string | null {
  const queryString = location.split('?')[1] || '';
  const params = new URLSearchParams(queryString);
  return params.get(param);
}

function pluckVideoContributor(location: string): string | null {
  const segments = pathSegments(location);
  const [first, second, contributor] = segments;

  if (first === 'video' && second === 'portfolio' && contributor) {
    return decodeURIComponent(contributor);
  }
  return null;
}

// Breadcrumb generation functions
function generateVideoBreadcrumbs(location: string): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [{ title: 'Video', path: '/video' }];
  const segments = pathSegments(location);

  // Details page
  if (segments[1] === 'stock') {
    // For client-side fallback, we can't fetch stock item data
    // Show generic breadcrumb - PHP will provide actual title
    breadcrumbs.push({ title: 'Stock Item' });
    return breadcrumbs;
  }

  // Collections page
  if (segments[1] === 'collections') {
    breadcrumbs.push({ title: 'Collections', path: '/video/collections' });
    return breadcrumbs;
  }

  // Portfolio/contributor
  const contributor = pluckVideoContributor(location);
  if (contributor) {
    breadcrumbs.push({ title: contributor });
    return breadcrumbs;
  }

  // Search and category pages
  const searchTerm = pluckSearchTerm(location);

  if (location.startsWith('/video/')) {
    if (searchTerm) {
      breadcrumbs.push({ title: 'Footage Search', path: '/video/search' });
    } else {
      breadcrumbs.push({ title: 'Footage', path: '/video/footage' });
    }
  } else if (location.startsWith('/motion-graphics/')) {
    if (searchTerm) {
      breadcrumbs.push({
        title: 'Motion Graphics Search',
        path: '/motion-graphics/search',
      });
    } else {
      breadcrumbs.push({
        title: 'Motion Graphics',
        path: '/video/motion-backgrounds',
      });
    }
  } else if (location.startsWith('/templates/')) {
    if (searchTerm) {
      breadcrumbs.push({
        title: 'Templates Search',
        path: '/templates/search',
      });
    } else {
      breadcrumbs.push({ title: 'Templates', path: '/video/templates' });
    }
  }

  if (searchTerm) {
    breadcrumbs.push({ title: searchTerm });
  }

  return breadcrumbs;
}

function generateAudioBreadcrumbs(location: string): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [{ title: 'Audio', path: '/audio' }];
  const segments = pathSegments(location);

  // Details page
  if (segments[1] === 'stock') {
    // For client-side fallback, we can't fetch stock item data
    // Show generic breadcrumb - PHP will provide actual title
    breadcrumbs.push({ title: 'Stock Item' });
    return breadcrumbs;
  }

  // Collections page
  if (segments[1] === 'collections') {
    breadcrumbs.push({ title: 'Collections', path: '/audio/collections' });
    return breadcrumbs;
  }

  // Search pages
  if (location.startsWith('/audio/search')) {
    const searchTerm = pluckSearchTerm(location);
    const contentTypeParam = getQueryParam(location, 'media-type');

    const contentTypeDisplay =
      contentTypeParam === 'music'
        ? 'Music'
        : contentTypeParam === 'sound-effects'
        ? 'Sound Effects'
        : null;

    if (contentTypeParam && contentTypeDisplay) {
      if (searchTerm) {
        breadcrumbs.push({
          title: `${contentTypeDisplay} Search`,
          path: `/audio/search?media-type=${contentTypeParam}`,
        });
      } else {
        breadcrumbs.push({
          title: contentTypeDisplay,
          path: `/audio/${contentTypeParam}`,
        });
      }
    }

    if (searchTerm) {
      breadcrumbs.push({ title: searchTerm });
    }
  }

  return breadcrumbs;
}

function generateImageBreadcrumbs(location: string): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [{ title: 'Images', path: '/images' }];
  const segments = pathSegments(location);

  // Details page
  if (segments[1] === 'stock') {
    // For client-side fallback, we can't fetch stock item data
    // Show generic breadcrumb - PHP will provide actual title
    breadcrumbs.push({ title: 'Stock Item' });
    return breadcrumbs;
  }

  // Collections page
  if (segments[1] === 'collections') {
    breadcrumbs.push({ title: 'Collections', path: '/images/collections' });
    return breadcrumbs;
  }

  // Search pages
  if (location.startsWith('/images/search')) {
    const searchTerm = pluckSearchTerm(location);
    const contentTypeParam = getQueryParam(location, 'media-type');

    const contentTypeDisplay =
      contentTypeParam === 'photos'
        ? 'Photos'
        : contentTypeParam === 'snapshots'
        ? 'Snapshots'
        : contentTypeParam === 'vectors'
        ? 'Vectors'
        : contentTypeParam === 'illustrations'
        ? 'Illustrations'
        : null;

    if (contentTypeParam && contentTypeDisplay) {
      if (searchTerm) {
        breadcrumbs.push({
          title: `${contentTypeDisplay} Search`,
          path: `/images/search?media-type=${contentTypeParam}`,
        });
      } else {
        breadcrumbs.push({
          title: contentTypeDisplay,
          path: `/images/${contentTypeParam}`,
        });
      }
    }

    if (searchTerm) {
      breadcrumbs.push({ title: searchTerm });
    }
  }

  return breadcrumbs;
}

// Main generation function
export function generateBreadcrumbs(location: string): BreadcrumbItem[] | null {
  const segments = pathSegments(location);
  const root = segments[0] || '';
  const contentType = segments[1] || null;

  const homeBreadcrumb: BreadcrumbItem = { title: 'Home', path: '/' };

  // Video pages
  if (
    root === 'video' &&
    contentType &&
    ['templates', 'footage', 'motion-backgrounds'].includes(contentType)
  ) {
    return null;
  }

  if (
    !location.startsWith('/video/upgrade-my-plan') &&
    !location.startsWith('/video/change-my-plan') &&
    !location.startsWith('/video/member/cancel') &&
    (root.includes('video') ||
      root === 'motion-graphics' ||
      root === 'templates')
  ) {
    return [homeBreadcrumb, ...generateVideoBreadcrumbs(location)];
  }

  // Audio pages
  if (
    root === 'audio' &&
    contentType &&
    ['music', 'sound-effects'].includes(contentType)
  ) {
    return null;
  }

  if (root === 'audio') {
    return [homeBreadcrumb, ...generateAudioBreadcrumbs(location)];
  }

  // Image pages
  if (
    root === 'images' &&
    contentType &&
    ['illustrations', 'vectors', 'photos'].includes(contentType)
  ) {
    return null;
  }

  if (root === 'images') {
    return [homeBreadcrumb, ...generateImageBreadcrumbs(location)];
  }

  return null;
}
